import { hasValue } from '@repo/utils';

import type { FactTrajectoryEntity } from '../../../service/entities';
import { sortTrajectoryPointsByMd } from '../../../utils/sort-trajectory-points-by-md';

export type TTrajectoryOrigin = {
  northing: number | null;
  easting: number | null;
  tvdss: number | null;
  /** Projected CRS identity from the origin station when available. */
  projectedCrsKey: string | null;
};

function readProjectedCrsKey(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

/**
 * Origin of a trajectory for overlay: first station by MD (MD≈0 / TVD≈0 — wellhead).
 * N/E and TVDSS are independent — a chart may need only one of them.
 * Uses northing/easting only (not coordinate1/coordinate2).
 * Projected CRS: origin station first, otherwise first non-empty CRS along the survey.
 */
export function getTrajectoryOrigin(entity: FactTrajectoryEntity): TTrajectoryOrigin | null {
  const pointsByMd = sortTrajectoryPointsByMd(entity.points);
  const originPoint = pointsByMd[0];

  if (!originPoint) {
    return null;
  }

  const { northing, easting, tvdss, projectedCrsKey } = originPoint.data;

  let resolvedCrs = readProjectedCrsKey(projectedCrsKey);

  if (!resolvedCrs) {
    for (const point of pointsByMd) {
      const key = readProjectedCrsKey(point.data.projectedCrsKey);

      if (key) {
        resolvedCrs = key;
        break;
      }
    }
  }

  return {
    northing: hasValue(northing) ? northing : null,
    easting: hasValue(easting) ? easting : null,
    tvdss: hasValue(tvdss) ? tvdss : null,
    projectedCrsKey: resolvedCrs,
  };
}

export function hasSpatialOrigin(origin: TTrajectoryOrigin): boolean {
  return hasValue(origin.northing) && hasValue(origin.easting);
}

export function hasVerticalOrigin(origin: TTrajectoryOrigin): boolean {
  return hasValue(origin.tvdss);
}

export function areProjectedCrsCompatible(left: string | null, right: string | null): boolean {
  if (!hasValue(left) || !hasValue(right)) {
    // Both missing → treat as compatible; one missing while the other is set → reject.
    return left === right;
  }

  return left === right;
}
